import { Component, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { UntypedFormGroup, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { QuestionBase } from '@jump-tech-frontend/domain';
import { Subject, takeUntil } from 'rxjs';
import { I18nKeys } from '@jump-tech-frontend/app-config';
import { AbstractDropdownQuestionComponent } from './abstract-dropdown-question.component';
import { QuestionHintComponent } from '@jump-tech-frontend/question-components';
import { JUI_NUMBER_INPUT_IMPORTS, JUI_SELECT_IMPORTS, JuiAlertBlock, JuiIcon } from '@jump-tech-frontend/core-components-angular';
import { QuestionLabelContentComponent } from '@jump-tech-frontend/question-components';
import { InlineSpinnerComponent } from '../inline-spinner-component/inline-spinner.component';

import { getDataSourceData } from '../list-data-source';
import { buildMessages } from './question.utils';

interface MultiQuestionItem {
  key: string;
  value: any;
  min?: string;
  type: string;
}

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'crds-question-multi-dropdown-value',
  template: `
    @if (optionsPopulated && show) {
      <div>
        @if (!initialised) {
          <div>
            @if (question.label) {
              <label class="form-label" [attr.for]="question.key" [attr.data-qa]="question.key">
                {{ question.label }}
                @if (question.required) {
                  <span> *</span>
                }
              </label>
            }
            <div class="form-control">
              <crds-inline-spinner
                [name]="question.key"
                [qaHook]="question.loadingQaHook"
                [type]="question.loadingType"
                [message]="question.loadingMessage"
                [colour]="question.loadingColour"
                [bgColour]="question.loadingBgColour"
                [textColour]="question.loadingTextColour"
                [center]="question.loadingCenter"
                >
              </crds-inline-spinner>
            </div>
          </div>
        }
        @if (initialised) {
          <div>
            @if (useJuiControls) {
              @if (question.label) {
                <label class="form-label">
                  <question-label-content
                    [question]="question"
                    [isInvalid]="isInvalid"
                    [i18ns]="i18ns"
                  ></question-label-content>
                </label>
              }
              @for (multiQuestion of multiQuestions; track $index) {
                <div class="jui-multi-item">
                  <div class="jui-multi-item__select-key">
                    <jui-select
                      size="sm"
                      [multiple]="false"
                      [placeholder]="question.placeholder || 'Click to select or type in a label'"
                      [dataqastring]="question.key + '_item_input' + '_' + $index"
                      [label]="question.dropdownItemLabel || 'Item'"
                      [(ngModel)]="multiQuestion.key"
                      (juiChange)="updateFormValue()"
                      [items]="juiSelectOptions"
                      [disable]="form.get(question.key).disabled"
                      [errors]="form.get(question.key)?.errors"
                      [validation]="'onLoad'"
                      [messages]="buildMessages(this.form, this.question, this.i18ns)"
                    ></jui-select>
                  </div>
                  <div class="jui-multi-item__number-value">
                    <jui-number-input
                      size="sm"
                      (juiChange)="updateFormValue()"
                      [(ngModel)]="multiQuestion.value"
                      [dataqastring]="question.key + '_value_input' + '_' + $index"
                      [label]="question.dropdownValueLabel || 'Value'"
                      [min]="minValue"
                      [format]="'float'"
                      [numstyle]="'decimal'"
                      [step]="0.01"
                      [disable]="form.get(question.key).disabled"
                      [errors]="form.get(question.key)?.errors"
                      [validation]="'onLoad'"
                      [messages]="{ required: 'required' }"
                    ></jui-number-input>
                  </div>
                  <div
            [class]="{
              'jui-multi-item__action': true,
              'jui-multi-item__action--error': form.get(question.key).errors !== null
            }"
                    >
                    <jui-icon
                      (click)="deleteQuestion($index)"
                      name="delete_forever"
                      size="sm"
                      [attr.data-qa]="question.key + '_delete' + '_' + $index"
                      >
                    </jui-icon>
                  </div>
                </div>
              }
              <jui-icon (click)="addQuestion()" name="playlist_add" size="sm" [attr.data-qa]="question.key + '_add'">
              </jui-icon>
            } @else {
              <div class="multiple-item-question" [attr.data-qa]="question.key + '_section'">
                <hr />
                @if (question.label) {
                  <label class="form-label">
                    <question-label-content
                      [question]="question"
                      [isInvalid]="isInvalid"
                      [i18ns]="i18ns"
                    ></question-label-content>
                  </label>
                }
                @for (multiQuestion of multiQuestions; track multiQuestion; let i = $index) {
                  <div class="multiple-item">
                    <div class="multiple-item-row">
                      <div class="multiple-item-key">
                        <div>{{ question.dropdownItemLabel || 'Item' }}:</div>
                        <input
                          type="text"
                          [attr.list]="'dataList_' + question.key + '_' + i"
                          class="form-control multiple-item-key"
                          [(ngModel)]="multiQuestion.key"
                          [placeholder]="question.placeholder || 'Click to select or type in a label'"
                          [attr.data-qa]="question.key + '_item_input' + '_' + i"
                          (change)="updateFormValue()"
                          />
                        <datalist id="{{ 'dataList_' + question.key + '_' + i }}">
                          @for (opt of question.options; track opt) {
                            <option [value]="opt.key">{{ opt.value }}</option>
                          }
                        </datalist>
                      </div>
                      <div class="multiple-item-value">
                        <div>{{ question.dropdownValueLabel || 'Value' }}:</div>
                        <input
                          class="form-control multiple-item-value"
                          [(ngModel)]="multiQuestion.value"
                          (change)="updateFormValue()"
                          [type]="multiQuestion.type || 'number'"
                          [attr.data-qa]="question.key + '_value_input' + '_' + i"
                          [attr.min]="getMinAttribute(multiQuestion)"
                          />
                      </div>
                      <div class="multiple-item-action">
                        <jui-icon
                          (click)="deleteQuestion(i)"
                          name="delete_forever"
                          size="sm"
                          [attr.data-qa]="question.key + '_delete' + '_' + i"
                          >
                        </jui-icon>
                      </div>
                    </div>
                  </div>
                }
                <jui-icon (click)="addQuestion()" name="playlist_add" size="sm" [attr.data-qa]="question.key + '_add'">
                </jui-icon>
                <!-- QUESTION HINT -->
                <question-hint [question]="question"></question-hint>
                @if (alert) {
                  <p>
                    <jui-alert-block iconName="info" size="sm">
                      {{ question.alert[form.get(question.key).value] }}
                    </jui-alert-block>
                  </p>
                }
              </div>
            }
          </div>
        }
      </div>
    }
    `,
  styles: [
    `
      .multiple-item {
        margin-top: 0.5rem !important;
        margin-bottom: 0.5rem !important;
      }
      .multiple-item-value {
        margin-left: 0.5rem;
        margin-right: 0.8rem;
      }
      .multiple-item-value > div {
        margin-left: 8px;
      }
      .multiple-item-key {
        width: 100%;
      }
      .multiple-item-key input::placeholder {
        color: grey;
        font-style: italic;
      }
      .multiple-item-row {
        background-color: inherit;
        padding: 0;
        display: flex;
        width: 100%;
        align-items: end;
      }
      .multiple-item-action {
        &--error {
          align-self: center;
        }
      }
      .jui-multi-item {
        display: flex;
        width: 100%;
        align-items: end;
        gap: var(--jds-space-sm);
        margin-top: var(--jds-space-sm);

        &__select-key {
          flex-grow: 1;
        }

        &__number-value {
          flex-basis: 10%;
        }

        &__action {
          &--error {
            align-self: center;
          }
        }
      }
      hr {
        margin-top: 0.5rem !important;
        margin-bottom: 0.5rem !important;
      }
    `
  ],
  imports: [
    InlineSpinnerComponent,
    QuestionLabelContentComponent,
    ReactiveFormsModule,
    FormsModule,
    ...JUI_SELECT_IMPORTS,
    ...JUI_NUMBER_INPUT_IMPORTS,
    JuiIcon,
    JuiAlertBlock,
    QuestionHintComponent
]
})
export class MultiDropdownWithValueQuestionComponent
  extends AbstractDropdownQuestionComponent
  implements OnChanges, OnDestroy
{
  @Input() override form: UntypedFormGroup;
  @Input() override question: QuestionBase<any>;
  @Input() i18ns: I18nKeys;
  @Input() useJuiControls = false;
  multiQuestions: MultiQuestionItem[] = [];
  private _unsubscribeFormChanges$ = new Subject();
  private sourceData = getDataSourceData();
  juiSelectOptions: { value: string; label: string }[] = [];
  minValue = 0;

  async ngOnChanges(changes: SimpleChanges) {
    if (!this.question || !Object.prototype.hasOwnProperty.call(changes, 'question')) {
      return;
    }

    if (this.question.datasource) {
      if (!this.question.datasource.transform) {
        this.question.datasource.transform =
          '[].{ text: join(`" - "`, [parent.name, name]), value: join(`" - "`, [parent.name, name]) }';
      }
      await this.spinnerService.show(this.question.key);
      this.mapData(
        await this.sourceData.getData(this.question.datasource),
        this.question.datasource,
        this.question,
        this.form
      );
      this.render();
      await this.spinnerService.hide(this.question.key);
    } else {
      this.render();
    }
    this.form.valueChanges.pipe(takeUntil(this._unsubscribeFormChanges$)).subscribe(x => {
      if (this.hasChanged()) {
        this.multiQuestions =
          this.form?.get(this.question.key)?.value && Array.isArray(this.form?.get(this.question.key)?.value)
            ? this.form?.get(this.question.key)?.value
            : [];
      }
    });
  }

  ngOnDestroy() {
    this._unsubscribeFormChanges$.next(null);
    this._unsubscribeFormChanges$.complete();
  }

  render() {
    this.multiQuestions =
      this.form?.get(this.question.key)?.value && Array.isArray(this.form?.get(this.question.key)?.value)
        ? this.form?.get(this.question.key)?.value
        : [];
    this.juiSelectOptions = this.question.options
      ? this.question.options.map(opt => {
          return {
            value: opt.key,
            label: opt.value
          };
        })
      : [];

    if (this.question.min) {
      this.minValue = parseInt(this.question.min);
    }
    this.initialised = true;

    setTimeout(() => {
      this.updateFormValue();
    });
  }

  updateFormValue() {
    const answers = this.getAnswers(this.multiQuestions);
    console.log(answers);
    this.form?.get(this.question.key)?.patchValue(answers);
  }

  private getAnswers(values: MultiQuestionItem[]) {
    const answers = values.filter(a => a.key);
    for (const answer of answers) {
      const standardAnswer = this.question.options ? this.question.options.find(a => a.key === answer.key) : undefined;
      answer.type = standardAnswer ? standardAnswer.type : this.question.defaultType || 'number';
    }
    return answers;
  }

  hasChanged() {
    const newMultiQuestions =
      this.form?.get(this.question.key)?.value && Array.isArray(this.form?.get(this.question.key)?.value)
        ? this.form?.get(this.question.key)?.value
        : [];
    const newAnswers = this.getAnswers(newMultiQuestions);
    const answers = this.getAnswers(this.multiQuestions);
    const One = newAnswers.every(x => answers.some(y => x.key === y.key && x.value === y.value));
    const Two = answers.every(x => newAnswers.some(y => x.key === y.key && x.value === y.value));
    return !One || !Two;
  }

  addQuestion() {
    this.multiQuestions.push({
      key: null,
      value: null,
      type: null
    });
    this.updateFormValue();
  }

  deleteQuestion(i) {
    this.multiQuestions.splice(i, 1);
    this.updateFormValue();
  }

  get optionsPopulated(): boolean {
    if (this.question.datasource) {
      return true;
    }
    return this.question.options && this.question.options.length > 0;
  }

  get isInvalid() {
    return this.form?.get(this.question.key)?.touched && this.form?.get(this.question.key)?.invalid;
  }

  get show() {
    const showIfQuestionPopulated = this.question.showIf ? this.showIf : true;
    return (
      (this.question.showIfPopulated !== true || this.value) && showIfQuestionPopulated && this.question.show !== false
    );
  }

  get alert() {
    return this.question.alert && this.question.alert[this.form?.get(this.question.key)?.value];
  }
  getMinAttribute(multiQuestion: MultiQuestionItem) {
    if (multiQuestion.type === 'number' && this.question.min !== undefined) {
      return this.question.min;
    }
    return null;
  }

  protected readonly buildMessages = buildMessages;
}
